import { Box, Typography, IconButton, TableRow } from "@mui/material";
import { styled } from "@mui/material/styles";
import Warning from "../../icons/Warning";

// Shared styled component for warning icon
export const WarningIcon = styled(Warning)({
  fontSize: "18px",
  color: "#FB8C00",
  marginRight: "8px",
});

// Main section container with gray background
export const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  backgroundColor: "#f8f8f8",
  padding: "0 12px 12px 12px",
  borderRadius: "4px",
  border: "1px solid #e0e0e0",
  "&:last-child": {
    marginBottom: 0,
  },
}));

// Section header with full-width border
export const SectionHeader = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 700,
  letterSpacing: "1px",
  marginBottom: theme.spacing(3),
  marginLeft: theme.spacing(-3),
  marginRight: theme.spacing(-3),
  padding: "12px",
  textTransform: "uppercase",
  color: "#000",
  borderBottom: "1px solid #e0e0e0",
  backgroundColor: "#f8f8f8",
}));

// No data message text
export const NoDataText = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontStyle: "italic",
  backgroundColor: "white",
  color: "#999",
  padding: theme.spacing(3),
  textAlign: "center",
}));

// Horizontal row for attributes/parameters
export const HorizontalPropertyRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isDeleted" && prop !== "isAdded",
})<{
  isDeleted?: boolean;
  isAdded?: boolean;
}>(({ isDeleted, isAdded }) => ({
  display: "flex",
  alignItems: "center",
  padding: "12px",
  borderBottom: `1px solid #e0e0e0`,
  backgroundColor: isAdded ? "rgba(25, 118, 210, 0.08)" : "#ffffff",
  opacity: isDeleted ? 0.5 : 1,
  "&:last-child": {
    borderBottom: "none",
  },
}));

// Label for attributes/parameters
export const PropertyLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isDeleted",
})<{ isDeleted?: boolean }>(({ isDeleted }) => ({
  fontSize: "15px",
  fontWeight: 400,
  minWidth: "220px",
  color: isDeleted ? "#999" : "#000",
  textDecoration: isDeleted ? "line-through" : "none",
}));

// Value container for attributes/parameters
export const PropertyValue = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: "12px",
});

// Delete button (trash icon)
export const DeleteButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  color: "#d32f2f",
  "&:hover": {
    backgroundColor: "rgba(211, 47, 47, 0.04)",
  },
}));

// Undo button
export const UndoButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  color: "#1976d2",
  "&:hover": {
    color: "#2e7d32",
    backgroundColor: "rgba(46, 125, 50, 0.04)",
  },
}));

// Row for adding new items
export const AddRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>(({ theme, disabled }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(3),
  padding: "8px 12px",
  borderTop: `1px solid #e0e0e0`,
  backgroundColor: "#ffffff",
  cursor: disabled ? "default" : "pointer",
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: disabled ? "#ffffff" : "#f5f5f5",
  },
  opacity: disabled ? 0.5 : 1,
}));

// Table row variant for adding items
export const AddTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>(({ theme, disabled }) => ({
  cursor: disabled ? "default" : "pointer",
  backgroundColor: "#ffffff",
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: disabled ? "#ffffff" : "#f5f5f5",
  },
  opacity: disabled ? 0.5 : 1,
}));

// Plus button for adding
export const AddButton = styled(IconButton)(({ theme }) => ({
  fontSize: "24px",
  lineHeight: 1,
  color: "#000",
  padding: theme.spacing(1),
  minWidth: 0,
  minHeight: 0,
  width: "auto",
  "&.Mui-disabled": {
    color: "#9e9e9e",
    opacity: 0.4,
    cursor: "default",
  },
}));

// Row for new items being added
export const NewPropertyRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(3),
  padding: "12px",
  borderTop: `1px solid #e0e0e0`,
  backgroundColor: "rgba(25, 118, 210, 0.08)",
}));

// Container for collapsible items (like processes)
export const CollapsibleContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  "&:last-child": {
    marginBottom: 0,
  },
}));

// Header row for collapsible items
export const CollapsibleRow = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isAdded" && prop !== "isDeleted" && prop !== "isExpanded",
})<{
  isAdded?: boolean;
  isDeleted?: boolean;
  isExpanded?: boolean;
}>(({ theme, isAdded, isDeleted, isExpanded }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2.5, 3),
  backgroundColor: isAdded
    ? "rgba(25, 118, 210, 0.08)"
    : isExpanded
    ? "#fafafa"
    : "#fff",
  border: "1px solid #e0e0e0",
  borderRadius: isExpanded ? "4px 4px 0 0" : "4px",
  cursor: "pointer",
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#fafafa",
  },
  marginBottom: isExpanded ? 0 : theme.spacing(2),
  opacity: isDeleted ? 0.5 : 1,
  textDecoration: isDeleted ? "line-through" : "none",
}));

// Left side content for collapsible row
export const CollapsibleRowLeft = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "16px",
});

// Name/title for collapsible item
export const CollapsibleName = styled(Typography)({
  fontSize: "15px",
  fontWeight: 500,
  color: "#000",
});

// Count/subtitle for collapsible item
export const CollapsibleCount = styled(Typography)({
  fontSize: "14px",
  fontWeight: 400,
  color: "#666",
});

// Content area for expanded collapsible item
export const CollapsibleContent = styled(Box)(({ theme }) => ({
  border: "1px solid #e0e0e0",
  borderTop: "none",
  borderRadius: "0 0 4px 4px",
  backgroundColor: "#fff",
}));
